<template>
    <Attr :widget_id="widget_id" :attr_id="attr_id">
        <!-- IE 2026 09 28 / ODOO-342342 (Use cases): вкладка Contents формы USE-CASE (контракт бэка v1 §4.2: attr_type AttrUseCaseMenu, binding _doc.items_view) —
             навигатор кейсов формой-меню (MenuPanel inline): у карты кейсов (uc-root) и раздела — разделы колонками, в них кейсы и пути; у пути — этапы и шаги
             с номерами; у кейса — якоря показа, вложенные кейсы, разделы инструкции. Бейдж — статус готовности (цвет записи статуса), «Demo check failed»,
             минуты, ▶ «Open in demo», ✓ «пройдено», звезда; поиск. Сверху — строка кейса: «Open in demo» / «Start path», минуты, прогресс пути,
             звезда и «пройдено» самой записи, «Restore my filters» (когда показ заменил мои чипсы), «Copy link». Логика — utils/useCaseMenu.js -->
        <div class="attr-uc-menu" ref="attr_container">
            <div class="ucm-bar">
                <!-- кнопки, которые бэк уже поставил в командную панель формы (guide.command_panel: button-guide-play / -fav / -done), здесь не дублируются -->
                <!-- IE 2026 09 28 / ODOO-342342 (контракт v1.2): заглушка скрытого кейса (Planned / Idea без прав, _doc.guide.not_available) — без «Open in demo», звезды и ✓ -->
                <template v-if="(isCase || isPath) && !notAvailable">
                    <button v-if="canPlay && !cmd.play" type="button" class="ucm-btn ucm-primary" :disabled="busy" :title="playTip" @click="onPlayRecord">
                        <i :class="busy ? 'pi pi-spin pi-spinner' : 'pi pi-play'"></i><span>{{ playLabel }}</span>
                    </button>
                    <span v-else-if="soonText" class="ucm-soon"><i class="pi pi-clock"></i> {{ soonText }}</span>
                    <span v-if="checkBadge" class="status-chip ucm-status" :class="checkBadge.cls" :title="checkBadge.title">{{ checkBadge.text }}</span>
                    <!-- IE 2026 09 28 / ODOO-342342 (контракт v1.1 §7): якоря с итогом проверки «unit» (эталон до смены единиц длительностей) — не провал; администратору — серая пилюля -->
                    <span v-if="unitText" class="status-chip ucm-status cell-background-grey" :title="t('The reference of this demo view predates the duration unit change of the database (days ↔ seconds): accept the reference again. The demo view itself is not broken')">{{ unitText }}</span>
                    <span v-if="minutesText" class="ucm-meta" :title="t('Duration')"><i class="pi pi-clock"></i>{{ minutesText }}</span>
                    <span v-if="progressText" class="ucm-meta ucm-progress" :title="t('My progress')">{{ progressText }}</span>
                    <button v-if="!cmd.fav" type="button" class="ucm-icon" :class="{ 'is-fav': recFav }" :title="recFav ? t('In favourites — click to remove') : t('Favourite use case')" @click="toggleRecFav">
                        <i :class="recFav ? 'pi pi-star-fill' : 'pi pi-star'"></i></button>
                    <button v-if="isCase && !cmd.done" type="button" class="ucm-icon" :class="{ 'is-done': !!recDone }" :title="recDone ? t('Done — click to unmark') : t('Mark as done')" @click="toggleRecDone">
                        <i :class="recDone ? 'pi pi-check-circle' : 'pi pi-circle'"></i></button>
                </template>
                <span v-else class="ucm-meta">{{ summaryText }}</span>
                <span class="ucm-spacer"></span>
                <button v-if="backups.length" type="button" class="ucm-btn" :title="t('Your filters were replaced by a demo view — bring them back')" @click="restoreFilters">
                    <i class="pi pi-filter-slash"></i><span>{{ t('Restore my filters') }}</span></button>
                <button v-if="recordId" type="button" class="ucm-icon" :title="t('Copy link')" @click="copyLink"><i class="pi pi-link"></i></button>
            </div>
            <div v-if="!items.length" class="ucm-empty">{{ emptyText }}</div>
            <!-- IE 2026 09 28 / ODOO-342342: колонка шире обычных 240 px — у пункта кейса название, минуты, пилюли статуса и три пометки (раскладка пункта — в стилях ниже) -->
            <!-- IE 2026 09 29 / ODOO-342781: favLocal — у роли «только чтение» звёзды и «пройдено» остаются (пометки живут в браузере), в остальных меню личные пометки скрыты -->
            <MenuPanel v-else inline :items="items" :runsTitle="runsTitle" search :searchPlaceholder="t('Search use cases…')" :favKey="favKey" favLocal :favOnlyDefault="false" :colWidth="300"
                :favOf="favOf" :doneOf="doneOf" :playOf="playOf" :epoch="epoch" :favTitle="t('Favourite use case')" help="useCases"
                @pick="onPick" @fav="onFav" @done="onDone" @play="onPlay" />
        </div>
    </Attr>
</template>

<script>
// IE 2026 09 28 / ODOO-342342 (Use cases): связка формы-меню кейсов с виджетом формы. Строки — store.get_value(record, binding) (по умолчанию _doc.items_view),
// пункты — utils/useCaseMenu.useCaseMenu_flatten; личные отметки — store.guideMarks (utils/guidePersonal.js: оптимистично, с откатом), начальные значения —
// поля fav / done строк и _doc.personal записи; «Open in demo» — store.guide_play (utils/guidePlay.js → play_plan бэка). Выключатель fe.guide.cases:
// false — вкладка спрятана (AttrTabPanel.isTabVisible → guideCases_tabHidden), компонент ничего не делает.
import { useMainStore } from '@/stores/mainStore'
import { useCaseMenu_flatten, useCaseMenu_action, useCaseMenu_playParams, useCaseMenu_summary, useCaseMenu_minutesText, ucRow_checkBadge, CHECK_FAILED } from '@/utils/useCaseMenu'
import { marks_applyRecord, marks_fav, marks_done, marks_pos } from '@/utils/guidePersonal'
import { guidePath_fromRows, guidePath_fromPlan, guidePath_progress, guidePath_progressText, guidePath_startIndex } from '@/utils/guidePath'   // IE 2026 09 28 / ODOO-342342: + guidePath_startIndex (приёмка Н2)
import { guideCases_on, UC_SEGMENT } from '@/utils/guideCases'

export default {
    props: ['widget_id', 'attr_id'],
    data: () => ({
        WIDGET: {},
        attr: {},   // приходит из store.widget_attr_set
        tick: 0,
        busy: false,
    }),
    setup() {
        const store = useMainStore()
        return { store }
    },
    created() {
        this.WIDGET = this.store.findWidget(this.widget_id).WIDGET
        this.content_Changed()
        try { if (this.on) this.store.guide_marksEnsure() } catch (e) { /* без слоя local */ }   // IE 2026 09 28 / ODOO-342342 (Н1): отметки, не принятые бэком, — из браузера
        this.personal_take()
    },
    mounted() { this.dicts_request() },
    watch: {
        // перечитывание записи (reload / Update data) — строки и личное заново
        'WIDGET.loading'(v) { if (!v) { this.tick++; this.$nextTick(() => { this.personal_take(); this.dicts_request() }) } },
        recordId() { this.personal_take() },
    },
    computed: {
        on() { void this.store._settingsEpoch; return guideCases_on(this.store) },
        binding() { return String(this.attr?.binding || '_doc.items_view') },
        record() { void this.tick; const r = this.WIDGET && this.WIDGET.record; return (r && typeof r === 'object') ? r : null },
        recordId() { return String(this.record?.id || '') },
        kind() { return String(this.record?.doc?.kind || (this.recordId === 'uc-root' ? 'section' : '')) },
        isCase() { return this.kind === 'case' },
        isPath() { return this.kind === 'path' },
        rows() {
            void this.tick
            // вотчеры Options API создаются РАНЬШЕ created() — первый прогон при WIDGET = {}: без записи строк нет
            const rec = this.record
            if (!rec) return []
            const v = this.store.get_value(rec, this.binding)
            return Array.isArray(v) ? v : []
        },
        marks() { return this.store.guideMarks },
        // кнопки командной панели формы от бэка (guide.command_panel) — те же действия (через store.guide_play / guide_mark: оптимистично), в строке меню не дублируются
        cmd() {
            void this.tick
            const names = new Set((this.WIDGET?.groupMenu?.commandPanel || []).map(b => b && b.name).filter(Boolean))
            return { play: names.has('button-guide-play'), fav: names.has('button-guide-fav') || names.has('button-guide-unfav'), done: names.has('button-guide-done') || names.has('button-guide-undone') }
        },
        epoch() { void this.store._favEpoch; return (this.marks?.epoch || 0) + this.tick },
        items() {
            void this.store.itemsSource_change_index   // словарь STATUS приходит позже строк — названия и цвета статусов
            if (!this.on || this.notAvailable) return []
            return useCaseMenu_flatten(this.rows, this.ctx())
        },
        runsTitle() { return this.isCase ? this.t('Demo views and links') : (this.isPath ? this.t('Steps') : this.t('Use cases')) },
        favKey() { return 'guide:' + this.recordId },
        emptyText() {
            if (this.record?._doc?.empty_text) return String(this.record._doc.empty_text)   // бэк (карта кейсов пустой базы): уже переведено
            if (this.isCase) return this.t('Nothing is linked to this use case yet')
            if (this.isPath) return this.t('This path has no steps yet')
            return this.t('No use cases in this database yet')
        },
        backups() { void this.store.guideBackups; void this.store.guideSession; return this.on ? this.store.guide_backups() : [] },
        // строка записи: играбельность, «скоро», проверка, минуты, прогресс пути
        soon() { const st = String(this.record?.doc?.status || ''); return st === 'uc-status-planned' || st === 'uc-status-idea' },
        canPlay() {
            if (!this.on || this.soon || this.notAvailable) return false
            if (this.isPath) return this.pathSteps.some(s => s.playable && !s.soon) || this.rows.some(r => r && r.playable)
            return this.rows.some(r => r && r.playable && !r.soon) || !!this.record?.doc?.demo?.workspace
        },
        soonText() { return this.soon ? this.t('Coming soon: not in the demo yet') : '' },
        playLabel() {
            if (!this.isPath) return this.t('Open in demo')
            const n = this.resumeStep
            return n ? this.t('Continue: step {n}', { n }) : this.t('Start path')
        },
        playTip() { return this.isPath ? this.t('Go through the path step by step in the live demo') : this.t('Open the workspace and page of this use case with its filters') },
        resumeStep() { if (!this.isPath) return null; void this.epoch; const p = marks_pos(this.marks, this.recordId, this.record?._doc?.personal?.pos); return (p && p > 1) ? p : null },
        // IE 2026 09 28 / ODOO-342342 (приёмка Н2): с какого шага «Start path» — место в пути, иначе первый показываемый шаг (не Planned / Idea; с якорем, если такие
        // есть); номер уходит в play_plan явно — прежний бэк (pick_step: «первый играбельный, иначе первый») без якорей стартовал с «Coming soon»
        startStep() { if (!this.isPath) return null; const st = this.pathSteps; const i = guidePath_startIndex(st, this.resumeStep); return i >= 0 ? st[i].n : null },
        checkBadge() {
            const c = this.record?._doc?.check || {}
            const st = String(c.status || this.record?.doc?.check_status || '')
            return st === CHECK_FAILED ? ucRow_checkBadge({ check_status: st, check: c }, { t: this.t }) : null
        },
        rightsEdit() { return !!this.record?._doc?.guide?.rights?.edit },
        // IE 2026 09 28 / ODOO-342342 (контракт v1.2): кейс Planned / Idea, открытый по id без rights.roadmap, приходит заглушкой — _doc.guide.not_available, текст в _doc.empty_text
        notAvailable() { return !!this.record?._doc?.guide?.not_available },
        // IE 2026 09 28 / ODOO-342342 (контракт v1.2 §7): итог проверки якорей по id (_doc.check.anchors: status diff / error / missing / unit, summary — причина) — подсказка «Demo check failed» у строки якоря (rights.edit)
        anchorCheck() {
            const list = this.record?._doc?.check?.anchors
            return new Map(Array.isArray(list) ? list.filter(a => a && a.id).map(a => [String(a.id), { status: String(a.status || ''), summary: String(a.summary || '') }]) : [])
        },
        // якоря с итогом ночной проверки «unit» (_doc.check.anchors[].status) — показываются только тому, кто может принять эталон (rights.edit)
        unitIds() {
            const list = this.record?._doc?.check?.anchors
            return new Set(Array.isArray(list) ? list.filter(a => a && a.status === 'unit' && a.id).map(a => String(a.id)) : [])
        },
        unitText() { return (this.rightsEdit && this.unitIds.size) ? this.t('Demo views with a reference before the unit change: {n}', { n: this.unitIds.size }) : '' },
        minutesText() {
            const m = Number(this.record?.doc?.duration_min) || Number(this.record?._doc?.duration_min) || 0
            return m ? useCaseMenu_minutesText(m, this.t) : ''
        },
        // шаги пути: _doc.steps бэка (те же, что path.steps ответа play_plan), иначе — строки меню с номерами
        pathSteps() {
            if (!this.isPath) return []
            const st = this.record?._doc?.steps
            return Array.isArray(st) && st.length ? guidePath_fromPlan({ steps: st }) : guidePath_fromRows(this.rows)
        },
        progressText() {
            if (!this.isPath || !this.pathSteps.length) return ''
            void this.epoch
            const p = guidePath_progress(this.pathSteps, (id) => !!this.doneValue(id, null))
            return guidePath_progressText(p, this.t, { minutes: !this.minutesText })   // минуты пути уже стоят рядом — не повторять
        },
        summaryText() {
            void this.epoch
            const s = useCaseMenu_summary(this.rows, (id, r) => !!this.doneValue(id, r))
            if (!s.cases) return ''
            return this.t('Use cases: {n} · done: {done}', { n: s.cases, done: s.done })
        },
        recFav() { void this.epoch; return !!marks_fav(this.marks, this.recordId, this.record?._doc?.personal?.fav) },
        recDone() { void this.epoch; return marks_done(this.marks, this.recordId, this.record?._doc?.personal?.done) },
    },
    methods: {
        content_Changed() { this.store.widget_attr_set(this) },
        dictRow(segment, id) {
            const IS = this.store.itemsSource?.[segment]
            if (!IS || id == null || id === '') return null
            return IS.ids?.[id] || (IS.data || []).find(x => x && String(x.id) === String(id)) || null
        },
        ctx() {
            return { t: (text, params) => this.t(text, params), dictRow: (segment, id) => this.dictRow(segment, id), cardId: this.recordId, cardKind: this.kind,
                unitIds: this.unitIds, showUnit: this.rightsEdit, anchorCheck: this.anchorCheck }
        },
        // словарь статусов — для цвета, когда строка не несёт cssClass-status (бэк кладёт его явно; словарь — запасной путь)
        dicts_request() {
            if (!this.on || !this.rows.length) return
            if (this.rows.some(r => r && r.status && !r['cssClass-status'])) {
                try { this.store.get_itemsSource(this, {}, { attr_type: 'AttrLink', relation_segment: 'STATUS' }, { fetchMode: 'all' }) } catch (e) { /* без словаря — текст статуса */ }
            }
        },
        // личное открытой записи (_doc.personal: fav, done, pos) — в общий слой отметок
        personal_take() {
            const p = this.record?._doc?.personal
            if (!this.on || !this.recordId || !p || typeof p !== 'object') return
            marks_applyRecord(this.store.guide_marksEnsure(), this.recordId, p)   // IE 2026 09 28 / ODOO-342342 (Н1): с не принятыми бэком отметками из браузера
            // IE 2026 09 29 / ODOO-342342 (приёмка ie-growth7 З1): и обратно — итог слоёв в _doc.personal формы (кнопки шапки = навигатор). Обычно его уже поставил
            // store.guide_personalTake при приходе записи; здесь — для кадра формы из снапшота (R6) и прочих путей прихода записи
            this.store.guide_syncPersonal(this.recordId)
        },
        doneValue(id, row) { return marks_done(this.marks, id, row ? row.done : null) },
        rowOf(raw) { return raw && raw._row },
        favOf(raw) {
            const u = raw && raw._uc
            if (!u || !u.record || u.kind === 'section') return null
            return !!marks_fav(this.marks, u.id, this.rowOf(raw)?.fav)
        },
        doneOf(raw) {
            const u = raw && raw._uc
            if (!u || u.kind !== 'case' || u.soon) return null
            return !!this.doneValue(u.id, this.rowOf(raw))
        },
        playOf(raw) { const u = raw && raw._uc; return !!(u && u.playable && !u.soon && this.on) },
        onFav(raw, on) { const u = raw && raw._uc; if (u && u.id) this.store.guide_mark('fav', u.id, on) },
        onDone(raw, on) { const u = raw && raw._uc; if (u && u.id) this.store.guide_mark('done', u.id, on) },
        toggleRecFav() { if (this.recordId) this.store.guide_mark('fav', this.recordId, !this.recFav) },
        toggleRecDone() { if (this.recordId) this.store.guide_mark('done', this.recordId, !this.recDone) },
        async play(params) {
            if (!params || this.busy) return
            this.busy = true
            try { await this.store.guide_play(this.WIDGET?.vueObj || this, params) } catch (e) { console.warn('AttrUseCaseMenu: play failed', e) } finally { this.busy = false }
        },
        onPlay(raw) { const u = raw && raw._uc; if (u) this.play(useCaseMenu_playParams(u)) },
        onPlayRecord() {
            if (!this.recordId) return
            const params = { id: this.recordId }
            if (this.isPath && this.startStep) params.step = this.startStep   // IE 2026 09 28 / ODOO-342342 (приёмка Н2): место в пути или первый показываемый шаг
            this.play(params)
        },
        // клик по пункту: кейс / путь / раздел — карточка записи подчинённым виджетом формы; шаг — «Open in demo»; раздел инструкции — читалка
        onPick(raw) {
            const a = useCaseMenu_action(raw)
            if (!a) return
            const vueObj = this.WIDGET?.vueObj || this
            try {
                if (a.type === 'open') {
                    this.store.executeStoreMethod(vueObj, { command: 'widget_open', widget: 'widget-record', _activate: true, open_in_children_widget: true,
                        item: { id: a.id, segment: UC_SEGMENT, title: raw.title || a.id } })
                } else if (a.type === 'play') {
                    this.play(a.params)
                } else if (a.type === 'docs') {
                    this.store.executeStoreMethod(vueObj, { command: 'widget_open', widget: 'widget-docs', params: { doc_link: a.link } })
                }
            } catch (e) { console.warn('AttrUseCaseMenu: pick failed', e) }
        },
        restoreFilters() { this.store.guide_restoreFilters() },
        copyLink() { this.store.guide_copyLink(this.recordId) },
    },
}
</script>

<style scoped>
.attr-uc-menu { min-height: 60px; padding: 2px 0 6px; display: flex; flex-direction: column; }
.ucm-bar { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; padding: 4px 18px 8px; border-bottom: 1px solid var(--surface-border, #dee2e6); margin-bottom: 6px; font-size: 12px; }
.ucm-spacer { flex: 1 1 auto; }
.ucm-btn { display: inline-flex; align-items: center; gap: 5px; height: 26px; padding: 0 10px; border-radius: 6px; font: inherit; font-size: 12px; cursor: pointer;
    border: 1px solid var(--surface-border, #dee2e6); background: var(--surface-ground, #f8f9fa); color: var(--text-color, #495057); white-space: nowrap; }
.ucm-btn:hover { border-color: var(--primary-color, #2997ff); }
.ucm-btn:disabled { opacity: .6; cursor: default; }
.ucm-btn .pi { font-size: 11px; }
.ucm-primary { background: var(--primary-color, #2997ff); border-color: var(--primary-color, #2997ff); color: var(--primary-color-text, #fff); font-weight: 600; }
.ucm-primary:hover { filter: brightness(1.05); }
.ucm-icon { display: inline-flex; align-items: center; justify-content: center; width: 26px; height: 26px; padding: 0; border-radius: 6px; cursor: pointer;
    border: 1px solid transparent; background: transparent; color: var(--text-color-secondary, #6c757d); }
.ucm-icon:hover { border-color: var(--surface-border, #dee2e6); color: var(--text-color, #495057); }
.ucm-icon.is-fav { color: #e0a800; }
.ucm-icon.is-done { color: #26852b; }
.ucm-meta { display: inline-flex; align-items: center; gap: 4px; color: var(--text-color-secondary, #6c757d); white-space: nowrap; }
.ucm-meta .pi { font-size: 11px; }
.ucm-progress { font-weight: 600; }
.ucm-soon { display: inline-flex; align-items: center; gap: 4px; color: var(--text-color-secondary, #6c757d); font-style: italic; }
.ucm-status { font-size: 11px; }
.ucm-empty { padding: 14px 18px; color: var(--text-color-secondary, #6b7280); font-size: 12px; }
/* IE 2026 09 28 / ODOO-342342: пункт кейса — в две строки. Первая: значок, номер шага, название, пометки ▶ ✓ ★; вторая, под названием: минуты и пилюли
   статуса («In demo», «Demo check failed»). В одну строку минуты, одна-две пилюли и три пометки сжимали название до 2–3 букв в строке (живой прогон 28.09,
   uc-en-1-root.png). Разрыв строки — ::after (flex-basis 100 %); у пункта без минут и пилюль добавленная строка нулевой высоты.
   Только эта панель: прочие панели MenuPanel не меняются */
.attr-uc-menu :deep(.menu-panel-col) { max-width: 100%; }   /* IE 2026 09 28 / ODOO-342342: узкий экран (телефон, 390px): колонка 300px не шире формы — иначе ▶ за краем (uc-uk-light-phone-phone-1-root.png) */
.attr-uc-menu :deep(.menu-panel-item) { flex-wrap: wrap; row-gap: 0; }   /* gap пункта (5px) иначе лёг бы и между добавленными строками */
.attr-uc-menu :deep(.menu-panel-item > .menu-panel-text) { flex: 1 1 0; }
.attr-uc-menu :deep(.menu-panel-item > .menu-panel-marks) { order: 1; }
.attr-uc-menu :deep(.menu-panel-item::after) { content: ''; order: 2; flex: 0 0 100%; height: 0; }
/* IE 2026 09 28 / ODOO-342342: отступ второй и следующих строк под название. Пункт верхнего уровня — отступом самого пункта (значок 12px + gap 5px; с номером
   шага — и номер), первый элемент (значок) вынесен назад отрицательным полем: так под названием встаёт и третья строка, когда длинная пилюля
   (uk «Перевірка демо не пройдена») не помещается во вторую (живой прогон uk 28.09, uc-uk-light-1-root.png). Вложенный пункт (отступ вложенности —
   inline padding-left от MenuPanel) — прежним распоркой ::before во второй строке */
.attr-uc-menu :deep(.menu-panel-item.menu-panel-depth-0) { padding-left: 25px; }
.attr-uc-menu :deep(.menu-panel-item.menu-panel-depth-0 > :first-child) { margin-left: -17px; }
.attr-uc-menu :deep(.menu-panel-item.menu-panel-depth-0:has(> .menu-panel-num)) { padding-left: calc(31px + 1.7ch); }
.attr-uc-menu :deep(.menu-panel-item.menu-panel-depth-0:has(> .menu-panel-num) > :first-child) { margin-left: calc(-23px - 1.7ch); }
.attr-uc-menu :deep(.menu-panel-item:not(.menu-panel-depth-0)::before) { content: ''; order: 3; flex: 0 0 12px; height: 0; }
.attr-uc-menu :deep(.menu-panel-item:not(.menu-panel-depth-0):has(> .menu-panel-num)::before) { flex-basis: calc(18px + 1.7ch); }
.attr-uc-menu :deep(.menu-panel-item > .menu-panel-aside),
.attr-uc-menu :deep(.menu-panel-item > .menu-panel-badge),
.attr-uc-menu :deep(.menu-panel-item > .menu-panel-alt) { order: 4; margin-left: 0; }
/* IE 2026 09 28 / ODOO-342342 (приёмка Н2): шаг пути, которого в демо не показать (Planned / Idea, без якоря), — приглушён (useCaseMenu_item: cssClass-row) */
.attr-uc-menu :deep(.menu-panel-item.ucm-step-dim) { opacity: .5; }
.attr-uc-menu :deep(.menu-panel-item.ucm-step-dim:hover) { opacity: .8; }
/* пилюля статуса сжата transform: scale(.9) вокруг центра (.status-chip) — видимый левый край длинной пилюли уезжал на 5 % её ширины (uk: 10px) от начала строки */
.attr-uc-menu :deep(.menu-panel-item > .menu-panel-badge) { transform-origin: 0 50%; }
</style>
